import { createRouter, createWebHistory } from 'vue-router';
import AppLayout from '@/layout/AppLayout.vue';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { SUBSCRIPTION_GATE_ENABLED } from '@/config/temporaryGates';
import { organizationHasSubscription } from '@/composables/subscriptionGate';
import { productDetailsPath } from '@/utils/productLinks';

// Single instance shared across navigations so the composable's watchers are only registered once
let featureFlagsApi: ReturnType<typeof useFeatureFlags> | null = null;

/** Reachable without a subscription — the flow that gets you one, and the way back out. */
const SUBSCRIPTION_EXEMPT_PATHS = ['/subscribe', '/subscribe/success', '/loggedout'];

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'default',
            component: () => import('@/views/pages/Default.vue')
        },
        {
            path: '/app',
            component: AppLayout,
            children: [
                {
                    path: '/dashboard',
                    name: 'e-commerce',
                    meta: {
                        breadcrumb: [{name: 'Dashboard', path: '/dashboard'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/dashboards/Ecommerce.vue'),
                },
                {
                    path: '/reports',
                    name: 'reports',
                    meta: {
                        breadcrumb: [{name: 'Reports', path: '/reports'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/reports/Reports.vue'),
                },
                {
                    path: '/customers',
                    name: 'customers',
                    meta: {
                        breadcrumb: [{name: 'Customers', path: '/customers'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/customers/Customers.vue'),
                },
                {
                    // The cross-customer contacts list. Gated by the same Kinde flag as the menu
                    // item; with it off the guard below sends the caller to the dashboard.
                    path: '/contacts',
                    name: 'contacts',
                    meta: {
                        breadcrumb: [{name: 'Contacts', path: '/contacts'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'contacts-page'
                    },
                    component: () => import('@/views/contacts/Contacts.vue'),
                },
                {
                    path: '/customers/details/:customerNumber',
                    name: 'customer-details',
                    meta: {
                        breadcrumb:(route: { params: { customerNumber: string } }) => [{name: 'Customers', path: '/customers'}, {name: `Customer #${route.params.customerNumber}`, path: `/customers/details/${route.params.customerNumber}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/customers/CustomerDetails.vue'),
                },
                {
                    path: '/tasks',
                    name: 'tasks',
                    meta: {
                        breadcrumb: [{name: 'Tasks', path: '/tasks'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'tasks'
                    },
                    component: () => import('@/views/tasks/Tasks.vue'),
                },
                {
                    path: '/flipbooks',
                    name: 'flipbooks',
                    meta: {
                        breadcrumb: [{name: 'Flipbooks', path: '/flipbooks'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/flipbooks/Flipbooks.vue'),
                },
                {
                    path: '/flipbooks/:id',
                    name: 'flipbook-details',
                    meta: {
                        breadcrumb: (route: { params: { flipbookId: string } }) => [
                            {name: 'Flipbooks', path: '/flipbooks'},
                            {name: `Flipbook #${route.params.flipbookId}`, path: `/flipbooks/${route.params.flipbookId}`}
                        ],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/flipbooks/FlipbookDetails.vue')
                },
                {
                    path: '/products',
                    name: 'products',
                    meta: {
                        breadcrumb: [{name:'Products', path: '/products'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/products/Products.vue'),
                },
                {
                    path: '/product-families',
                    name: 'product-families',
                    meta: {
                        breadcrumb: [{name: 'Product Families', path: '/product-families'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/products/ProductFamilies.vue'),
                },
                {
                    path: '/product-families/:familyKey',
                    name: 'product-family-details',
                    meta: {
                        breadcrumb: (route: { params: { familyKey: string } }) => [{name: 'Product Families', path: '/product-families'}, {name: `Family: ${route.params.familyKey}`, path: `/product-families/${route.params.familyKey}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/products/ProductFamilyDetails.vue'),
                },
                {
                    // (.+) lets the SKU span a raw '/' (a pasted or bookmarked URL); links encode it.
                    // Not (.*): that also matches an empty SKU and would take /products/ off the list.
                    path: '/products/:sku(.+)',
                    name: 'product-details',
                    meta: {
                        breadcrumb: (route: { params: { sku: string } }) => [{name:'Products', path: '/products'},{name: `Product #: ${route.params.sku}`, path: productDetailsPath(route.params.sku)}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/products/ProductDetails.vue'),
                },
                {
                    // The manufacturers (lines) an agency represents. Agency organizations only:
                    // the guard below sends a Supplier to the dashboard, as the API would answer 403.
                    path: '/vendors',
                    name: 'vendors',
                    meta: {
                        breadcrumb: [{name: 'Vendors', path: '/vendors'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresAgency: true
                    },
                    component: () => import('@/views/vendors/Vendors.vue'),
                },
                {
                    path: '/vendors/new',
                    name: 'vendor-new',
                    meta: {
                        breadcrumb: [{name: 'Vendors', path: '/vendors'}, {name: 'New vendor', path: '/vendors/new'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresAgency: true
                    },
                    component: () => import('@/views/vendors/VendorDetails.vue'),
                },
                {
                    path: '/vendors/:vendorCode',
                    name: 'vendor-details',
                    meta: {
                        breadcrumb: (route: { params: { vendorCode: string } }) => [{name: 'Vendors', path: '/vendors'}, {name: route.params.vendorCode, path: `/vendors/${route.params.vendorCode}`}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresAgency: true
                    },
                    component: () => import('@/views/vendors/VendorDetails.vue'),
                },
                {
                    path: '/carts',
                    name: 'carts',
                    meta: {
                        breadcrumb: [{name: 'Carts', path: '/carts'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/carts/PendingOrders.vue'),
                },
                {
                    path: '/carts/:cartId',
                    name: 'cart-details',
                    meta: {
                        breadcrumb: (route: { params: { cartId: string } }) => [{name:'Carts', path: '/carts'},{name: `Cart #: ${route.params.cartId}`, path: `/carts/${route.params.cartId}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/carts/CartDetails.vue'), 
                },
                {
                    path: '/campaigns',
                    name: 'campaigns',
                    meta: {
                        breadcrumb: [{name: 'Campaigns', path: '/campaigns'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'campaigns'
                    },
                    component: () => import('@/views/campaigns/Campaigns.vue'),
                },
                {
                    path: '/campaigns/new',
                    name: 'campaign-new',
                    meta: {
                        breadcrumb: [{name: 'Campaigns', path: '/campaigns'}, {name: 'New campaign', path: '/campaigns/new'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'campaigns'
                    },
                    component: () => import('@/views/campaigns/CampaignDetails.vue'),
                },
                {
                    path: '/campaigns/:campaignId',
                    name: 'campaign-details',
                    meta: {
                        breadcrumb: (route: { params: { campaignId: string } }) => [{name: 'Campaigns', path: '/campaigns'}, {name: 'Campaign', path: `/campaigns/${route.params.campaignId}`}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'campaigns'
                    },
                    component: () => import('@/views/campaigns/CampaignDetails.vue'),
                },
                {
                    path: '/orders',
                    name: 'orders',
                    meta: {
                        breadcrumb: [{name: 'Orders', path: '/orders'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/orders/Orders.vue'),
                },
                {
                    path: '/orders/:orderNumber',
                    name: 'order-details',
                    meta: {
                        breadcrumb: (route: { params: { orderNumber: string } }) => [{name: 'Orders', path: '/orders'}, {name:`Order #: ${route.params.orderNumber}`, path: `/orders/${route.params.orderNumber}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/orders/Order.vue'),
                },
                {
                    path: '/invoices',
                    name: 'invoices',
                    meta: {
                        breadcrumb: [{name: 'Invoices', path: '/invoices'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/invoices/Invoices.vue'),
                },
                {
                    path: '/invoices/:invoiceId',
                    name: 'invoice-details',
                    meta: {
                        breadcrumb: (route: { params: { invoiceId: string } }) => [{name: 'Invoices', path: '/invoices'}, {name: `Invoice: ${route.params.invoiceId}`, path: `/invoices/${route.params.invoiceId}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/invoices/Invoice.vue'),
                },
                {
                    path: '/sales-reps',
                    name: 'sales-reps',
                    meta: {
                        breadcrumb: [{name:'Sales Reps', path: '/sales-reps'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/sales-reps/SalesReps.vue'),
                },
                {
                    path: '/access-requests',
                    name: 'access-requests',
                    meta: {
                        breadcrumb: [{name: 'Storefront Requests', path: '/access-requests'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/access-requests/AccessRequests.vue'),
                },
                {
                    path: '/access-requests/details/:accessRequestId',
                    name: 'access-request-details',
                    meta: {
                        breadcrumb: (route: { params: { accessRequestId: string } }) => [{name: 'Storefront Requests', path: '/access-requests'}, {name: 'Details', path: `/access-requests/details/${route.params.accessRequestId}`}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/access-requests/AccessRequestDetails.vue'),
                },
                {
                    path: '/storefront-users',
                    name: 'storefront-users',
                    meta: {
                        breadcrumb: [{name: 'Storefront Users', path: '/storefront-users'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/storefront-users/StorefrontUsers.vue'),
                },
                {
                    path: '/research-settings',
                    name: 'research-settings',
                    meta: {
                        breadcrumb: [{name: 'Research Settings', path: '/research-settings'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/research-settings/ResearchSettings.vue'),
                },
                {
                    path: '/website-api-key',
                    name: 'website-api-key',
                    meta: {
                        breadcrumb: [{name: 'Website API Key', path: '/website-api-key'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/website-api-key/WebsiteApiKey.vue'),
                },
                {
                    // The customer-user explainer page moved into the Import Data guide drawer;
                    // keep the old path working for anyone who bookmarked it.
                    path: '/customer-user-import',
                    name: 'customer-user-import',
                    redirect: { path: '/data', query: { import: 'Customer Users' } },
                },
                {
                    // API Access is now a section of Organization Settings; keep the old
                    // path and name working for anyone who bookmarked it.
                    path: '/api-access',
                    name: 'api-access',
                    redirect: '/settings',
                },
                {
                    path: '/integrations',
                    name: 'integrations',
                    meta: {
                        breadcrumb: [{name: 'Integrations', path: '/integrations'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/integrations/Integrations.vue'),
                },
                {
                    path: '/users',
                    name: 'users',
                    meta: {
                        breadcrumb: [{name: 'Users', path: '/users'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/users/Users.vue'),
                },
                {
                    path: '/billing',
                    name: 'billing',
                    meta: {
                        breadcrumb: [{name: 'Billing', path: '/billing'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/billing/Billing.vue'),
                },
                {
                    path: '/settings',
                    name: 'settings',
                    meta: {
                        breadcrumb: [{name: 'Settings', path: '/settings'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/settings/Settings.vue'),
                },
                {
                    path: '/quickbooks',
                    name: 'quickbooks',
                    meta: {
                        breadcrumb: [{name: 'Quickbooks', path: '/quickbooks'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/quickbooks/Quickbooks.vue'),
                },
                {
                    path: '/quickbooks/callback',
                    name: 'quickbooks-callback',
                    meta: {
                        breadcrumb: [{name: 'Quickbooks', path: '/quickbooks-callback'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/quickbooks/QuickbooksCallback.vue'),
                },
                {
                    path: '/data',
                    name: 'data',
                    meta: {
                        breadcrumb: [{name: 'Manage Data', path: '/data'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/data/ImportData.vue'),
                },
                {
                    path: '/images',
                    name: 'images',
                    meta: {
                        breadcrumb: [{name: 'Images', path: '/images'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/images/Images.vue')
                },
                {
                    path: '/categories',
                    name: 'categories',
                    meta: {
                        breadcrumb: [{name: 'Categories', path: '/categories'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/categories/Categories.vue')
                },
                {
                    path: '/tags',
                    name: 'tags',
                    meta: {
                        breadcrumb: [{name: 'Tags', path: '/tags'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/tags/Tags.vue')
                },
                {
                    path: '/email-deliveries',
                    name: 'email-deliveries',
                    meta: {
                        breadcrumb: [{name: 'Email Deliveries', path: '/email-deliveries'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/email-deliveries/EmailDeliveries.vue')
                },
                {
                    path: '/exports',
                    name: 'exports',
                    meta: {
                        breadcrumb: [{name: 'Exports', path: '/exports'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/exports/Exports.vue')
                },
                {
                    path: '/forecast-models',
                    name: 'forecast-models',
                    meta: {
                        breadcrumb: [{name: 'Forecast Models', path: '/forecast-models'}],
                        requiresAuth: true,
                        requiresAdmin: true,
                        requiresFeatureFlag: 'sales-forecasting'
                    },
                    component: () => import('@/views/forecasting/ForecastModels.vue')
                },
                {
                    path: '/company-docs',
                    name: 'company-docs',
                    meta: {
                        breadcrumb: [{name: 'Company Docs', path: '/company-docs'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/company-docs/CompanyDocs.vue')
                },
                {
                    path: '/docs/sql-connector',
                    name: 'sql-connector-docs',
                    meta: {
                        breadcrumb: [{name: 'SQL Connector', path: '/docs/sql-connector'}],
                        requiresAuth: true,
                        requiresAdmin: true
                    },
                    component: () => import('@/views/docs/SqlConnectorDocs.vue'),
                },
                {
                    path: '/pages/empty',
                    name: 'empty',
                    component: () => import('@/views/pages/Empty.vue')
                },
            ]
        },
        {
            path:'/subscribe',
            name: 'subscribe',
            meta: {
                breadcrumb: [{name: 'Subscribe', path: '/subscribe'}],
                requiresAuth: false,
                requiresAdmin: false
            },
            component: () => import('@/views/pages/Subscribe.vue')
        },
        {
            // Where Stripe sends the browser after a completed embedded checkout. The API builds this
            // URL from its own AppConfig:BaseUrl, so the path has to match on every environment.
            path:'/subscribe/success',
            name: 'subscribe-success',
            meta: {
                breadcrumb: [{name: 'Subscribe', path: '/subscribe'}],
                requiresAuth: false,
                requiresAdmin: false
            },
            component: () => import('@/views/pages/SubscribeSuccess.vue')
        },
        {
            path: '/loggedout',
            name: 'loggedout',
            component: () => import('@/views/pages/auth/LoggedOut.vue')
        },
        {
            path: '/sign-up',
            name: 'sign-up',
            component: () => import('@/views/pages/Sign-Up.vue')
        },
        {
            path: '/pages/notfound',
            name: 'notfound',
            component: () => import('@/views/pages/NotFound.vue')
        },
        {
            path: '/auth/callback',
            name: 'auth-callback',
            component: () => import('@/views/pages/auth/AuthCallback.vue')
        },
        {
            path: '/auth/login',
            name: 'login',
            component: () => import('@/views/pages/auth/Login.vue')
        },
        {
            path: '/auth/access',
            name: 'accessDenied',
            component: () => import('@/views/pages/auth/Access.vue')
        },
        {
            path: '/auth/download-app',
            name: 'download-app',
            component: () => import('@/views/pages/auth/DownloadApp.vue')
        },
    
        {
            path: '/:pathMatch(.*)*',
            name: 'notfound',
            component: () => import('@/views/pages/NotFound.vue')
        }
    ],
    scrollBehavior() {
        return { left: 0, top: 0 };
    }
});

// Navigation Guard
router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    
    // Special handling for default, home page, sign-up and auth-related routes
    if (to.path === '/' || to.path === '/sign-up' || to.path === '/loggedout' || to.path === '/home' || to.path.startsWith('/auth/')) {
        return next();
    }    await authStore.checkAuth();

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        return next('/');
    }
    if (to.meta.requiresAdmin && !authStore.admin) {
        return next('/auth/download-app');
    }
    // Gate everything behind having a subscription. Checked after auth, so an unauthenticated visitor is
    // sent to sign in rather than to a pricing page they cannot buy from.
    if (SUBSCRIPTION_GATE_ENABLED && authStore.isAuthenticated && !SUBSCRIPTION_EXEMPT_PATHS.includes(to.path)) {
        if (!(await organizationHasSubscription())) {
            return next('/subscribe');
        }
    }

    // Agency-only pages (Vendors): gated on the organization's type, not a feature flag. The
    // organization is read once if nothing has loaded it yet, so a deep link decides correctly.
    if (to.meta.requiresAgency) {
        if (!authStore.organization?.organizationId) {
            await authStore.getOrganization();
        }
        if (!authStore.isAgency) {
            return next('/dashboard');
        }
    }

    if (to.meta.requiresFeatureFlag) {
        if (!featureFlagsApi) {
            featureFlagsApi = useFeatureFlags();
        }
        await featureFlagsApi.refreshFeatureFlags();
        if (!featureFlagsApi.isFeatureEnabled(to.meta.requiresFeatureFlag as string, false)) {
            return next('/dashboard');
        }
    }
    
    // Clear Orders navigation state when leaving Orders section
    const isLeavingOrdersSection = from.path.startsWith('/orders') && !to.path.startsWith('/orders');
    if (isLeavingOrdersSection) {
        // Import here to avoid circular dependency
        const { useOrdersStore } = await import('@/stores/ordersStore');
        const ordersStore = useOrdersStore();
        ordersStore.clearStoredTableState();
    }

    // Clear Invoices navigation state when leaving Invoices section
    const isLeavingInvoicesSection = from.path.startsWith('/invoices') && !to.path.startsWith('/invoices');
    if (isLeavingInvoicesSection) {
        const { useInvoicesStore } = await import('@/stores/invoicesStore');
        const invoicesStore = useInvoicesStore();
        invoicesStore.clearStoredTableState();
    }

    // Clear Contacts navigation state when leaving the Contacts page
    const isLeavingContactsSection = from.path.startsWith('/contacts') && !to.path.startsWith('/contacts');
    if (isLeavingContactsSection) {
        const { useContactsStore } = await import('@/stores/contactsStore');
        const contactsStore = useContactsStore();
        contactsStore.clearStoredTableState();
    }

    // Clear Customers navigation state when leaving Customers section
    const isLeavingCustomersSection = from.path.startsWith('/customers') && !to.path.startsWith('/customers');
    if (isLeavingCustomersSection) {
        // Import here to avoid circular dependency
        const { useCustomerStore } = await import('@/stores/customerStore');
        const customerStore = useCustomerStore();
        customerStore.clearStoredTableState();
    }

    // Clear Tasks navigation state when leaving the Tasks section
    const isLeavingTasksSection = from.path.startsWith('/tasks') && !to.path.startsWith('/tasks');
    if (isLeavingTasksSection) {
        // Import here to avoid circular dependency
        const { useTasksStore } = await import('@/stores/tasksStore');
        const tasksStore = useTasksStore();
        tasksStore.clearStoredTableState();
    }
    
    // Clear Products navigation state when leaving Products section
    const isLeavingProductsSection = from.path.startsWith('/products') && !to.path.startsWith('/products');
    if (isLeavingProductsSection) {
        // Import here to avoid circular dependency
        const { useProductStore } = await import('@/stores/productStore');
        const productStore = useProductStore();
        productStore.clearStoredTableState();
    }
    
    // Clear Carts navigation state when leaving Carts section
    const isLeavingCartsSection = from.path.startsWith('/carts') && !to.path.startsWith('/carts');
    if (isLeavingCartsSection) {
        // Import here to avoid circular dependency
        const { useCartStore } = await import('@/stores/cartStore');
        const cartStore = useCartStore();
        cartStore.clearStoredTableState();
    }

    // Clear Integrations navigation state when leaving Integrations section
    const isLeavingIntegrationsSection = from.path.startsWith('/integrations') && !to.path.startsWith('/integrations');
    if (isLeavingIntegrationsSection) {
        // Import here to avoid circular dependency
        const { useIntegrationsStore } = await import('@/stores/integrationsStore');
        const integrationsStore = useIntegrationsStore();
        integrationsStore.clearStoredTableState();
    }

    // Clear Company Docs navigation state when leaving Company Docs section
    const isLeavingCompanyDocsSection = from.path.startsWith('/company-docs') && !to.path.startsWith('/company-docs');
    if (isLeavingCompanyDocsSection) {
        // Import here to avoid circular dependency
        const { useCompanyDocsStore } = await import('@/stores/companyDocsStore');
        const companyDocsStore = useCompanyDocsStore();
        companyDocsStore.clearStoredTableState();
    }

    next();
});

export default router;
